import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Combobox } from "@headlessui/react";
import {
  listSignalMatchesBySite,
  createSignalMatch,
  updateSignalMatch,
  deleteSignalMatch,
} from "../api/signalMatch";
import { listSites } from "../api/sites";
import { listSignalTypes } from "../api/signalTypes";
import { listArraysDetailedBySite } from "../api/arrays";
import { listArrayInvertersDetailedBySite } from "../api/arrayinverters";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const REQUIRED_HEADERS = ["tid", "name_short", "multiplier"];

export default function SignalMatchPage() {
  const [sites, setSites] = useState([]);
  const [signalTypes, setSignalTypes] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteQuery, setSiteQuery] = useState("");

  const [arrayOptions, setArrayOptions] = useState([]);
  const [inverterOptions, setInverterOptions] = useState([]);
  const [loadingTopology, setLoadingTopology] = useState(false);

  const [siteMatches, setSiteMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);

  const [sensorCards, setSensorCards] = useState([]);
  const [importStats, setImportStats] = useState({ unique: 0, duplicates: 0, alreadySaved: 0, needsReview: 0 });
  const [csvError, setCsvError] = useState("");
  const [banner, setBanner] = useState({ tone: "", text: "" });
  const [saving, setSaving] = useState(false);
  const [typeFilters, setTypeFilters] = useState([]);
  const [nameFilter, setNameFilter] = useState("");

  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [activeSignalId, setActiveSignalId] = useState(null);
  const [mappingDraft, setMappingDraft] = useState({ arrays: [], inverterId: "", multiplier: "1" });

  const fileInputRef = useRef(null);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const [siteList, sigTypes] = await Promise.all([listSites(), listSignalTypes()]);
        const sortedSites = (siteList || [])
          .slice()
          .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
        setSites(sortedSites);
        setSignalTypes(sigTypes || []);
      } catch (err) {
        console.error("Failed to load reference data", err);
      }
    };
    bootstrap();
  }, []);

  useEffect(() => {
    if (!selectedSite && sites.length > 0) {
      setSelectedSite(sites[0].id);
    }
  }, [sites, selectedSite]);

  const refreshSiteMatches = useCallback(async (siteId) => {
    if (!siteId) return;
    setMatchesLoading(true);
    try {
      const matches = await listSignalMatchesBySite(siteId);
      setSiteMatches(matches || []);
    } catch (err) {
      console.error("Failed to load signal matches", err);
    } finally {
      setMatchesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedSite) {
      setArrayOptions([]);
      setInverterOptions([]);
      setSiteMatches([]);
      setMatchesLoading(false);
      return;
    }

    setLoadingTopology(true);
    const loadTopology = async () => {
      try {
        const [arrays, inverterRows] = await Promise.all([
          listArraysDetailedBySite(selectedSite),
          listArrayInvertersDetailedBySite(selectedSite),
        ]);
        setArrayOptions(arrays || []);
        setInverterOptions(dedupeById(inverterRows || []));
      } catch (err) {
        console.error("Failed to load topology", err);
      } finally {
        setLoadingTopology(false);
      }
    };

    loadTopology();
    refreshSiteMatches(selectedSite);
  }, [selectedSite, refreshSiteMatches]);

  useEffect(() => {
    setSensorCards([]);
    setImportStats({ unique: 0, duplicates: 0, alreadySaved: 0, needsReview: 0 });
    setCsvError("");
    setBanner({ tone: "", text: "" });
    setMapModalOpen(false);
    setActiveSignalId(null);
    setSiteQuery("");
    setTypeFilters([]);
    setNameFilter("");
  }, [selectedSite]);

  const signalTypeByShort = useMemo(() => {
    const map = new Map();
    signalTypes.forEach((type) => {
      if (type.name_short) {
        map.set(type.name_short.toLowerCase(), type);
      }
    });
    return map;
  }, [signalTypes]);

  const signalTypeById = useMemo(() => {
    const map = new Map();
    signalTypes.forEach((type) => map.set(type.id, type));
    return map;
  }, [signalTypes]);

  const existingSourceNames = useMemo(() => {
    const set = new Set();
    siteMatches.forEach((match) => {
      if (match?.source_name) {
        set.add(match.source_name.trim().toLowerCase());
      }
    });
    return set;
  }, [siteMatches]);

  const sortedArrayOptions = useMemo(() => {
    return [...arrayOptions].sort((a, b) => compareArrayNames(a.array_name, b.array_name));
  }, [arrayOptions]);

  const sortedInverterOptions = useMemo(() => {
    return [...inverterOptions].sort((a, b) => compareArrayNames(a.inv_array_name || "", b.inv_array_name || ""));
  }, [inverterOptions]);

  const arrayNameMap = useMemo(() => {
    const map = new Map();
    arrayOptions.forEach((arr) => map.set(arr.id, arr.array_name));
    return map;
  }, [arrayOptions]);

  const inverterNameMap = useMemo(() => {
    const map = new Map();
    sortedInverterOptions.forEach((inv) => map.set(inv.id, inv.inv_array_name || inv.array_name));
    return map;
  }, [sortedInverterOptions]);

  const arrayLookupByName = useMemo(() => buildNameLookup(arrayOptions, (arr) => arr.array_name), [arrayOptions]);

  const inverterLookupByName = useMemo(
    () => buildNameLookup(sortedInverterOptions, (inv) => inv.inv_array_name || inv.array_name),
    [sortedInverterOptions]
  );

  const getSignalTypeForCard = useCallback((card) => {
    if (!card) return null;
    if (card.signalTypeId && signalTypeById.get(card.signalTypeId)) {
      return signalTypeById.get(card.signalTypeId);
    }
    if (card.shortName) {
      return signalTypeByShort.get(card.shortName.toLowerCase()) || null;
    }
    return null;
  }, [signalTypeById, signalTypeByShort]);

  const availableTypeFilters = useMemo(() => {
    const codes = new Set();
    sensorCards.forEach((card) => {
      const type = getSignalTypeForCard(card);
      const shortName = type?.name_short || card.shortName;
      if (shortName) {
        codes.add(shortName);
      }
    });
    return Array.from(codes).sort((a, b) => compareArrayNames(a, b));
  }, [sensorCards, getSignalTypeForCard]);

  useEffect(() => {
    setTypeFilters((prev) => prev.filter((code) => availableTypeFilters.includes(code)));
  }, [availableTypeFilters]);

  const readyCards = sensorCards.filter(
    (card) => card.arrays.length > 0 && getSignalTypeForCard(card) && !hasMappingIssues(card)
  );
  const saveDisabled = saving || !selectedSite || readyCards.length === 0;

  const filteredCards = useMemo(() => {
    if (sensorCards.length === 0) return [];
    const query = nameFilter.trim().toLowerCase();
    const filtered = sensorCards.filter((card) => {
      const type = getSignalTypeForCard(card);
      const shortCode = type?.name_short || card.shortName || "";
      if (typeFilters.length > 0 && (!shortCode || !typeFilters.includes(shortCode))) {
        return false;
      }
      if (query) {
        const haystack = `${card.tid} ${type?.name || ""} ${shortCode}`.toLowerCase();
        if (!haystack.includes(query)) {
          return false;
        }
      }
      return true;
    });
    return filtered.sort((a, b) => compareArrayNames(a.tid || "", b.tid || ""));
  }, [sensorCards, typeFilters, nameFilter, getSignalTypeForCard]);

  const openFileDialog = () => {
    if (!selectedSite) return;
    fileInputRef.current?.click();
  };

  const handleCsvSelection = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (loadingTopology) {
      setCsvError("Site topology is still loading. Please wait a moment and import again.");
      event.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result;
        const { records, duplicates, alreadySaved } = parseSignalCsv(text, {
          existingSources: existingSourceNames,
          arrayLookupByName,
          inverterLookupByName,
        });
        const normalizedRecords = records.map((record) => {
          const type = record.shortName
            ? signalTypeByShort.get(record.shortName.toLowerCase())
            : null;
          return {
            ...record,
            signalTypeId: type?.id || null,
          };
        });
        const needsReview = normalizedRecords.filter((record) => hasMappingIssues(record)).length;
        setSensorCards(normalizedRecords);
        setImportStats({ unique: normalizedRecords.length, duplicates, alreadySaved, needsReview });
        setCsvError("");
        setBanner({
          tone: needsReview > 0 ? "warning" : "success",
          text:
            needsReview > 0
              ? `${needsReview} imported signal${needsReview === 1 ? "" : "s"} need manual mapping review before saving.`
              : "CSV loaded successfully.",
        });
      } catch (err) {
        console.error("Failed to parse CSV", err);
        setSensorCards([]);
        setImportStats({ unique: 0, duplicates: 0, alreadySaved: 0, needsReview: 0 });
        setCsvError(err.message || "Unable to parse CSV file.");
      }
    };
    reader.onerror = () => {
      setCsvError("Unable to read the selected file.");
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const handleLoadSaved = () => {
    if (!selectedSite) return;
    if (!siteMatches.length) {
      setSensorCards([]);
      setImportStats({ unique: 0, duplicates: 0, alreadySaved: 0, needsReview: 0 });
      setBanner({ tone: "", text: "No mappings stored for this site yet." });
      return;
    }

    const cards = siteMatches.map((match) => {
      const type = signalTypeById.get(match.signal_type_id);
      const sourceName = (match.source_name || "").trim();
      return {
        id: match.id,
        tid: sourceName,
        shortName: type?.name_short || "",
        signalTypeId: match.signal_type_id,
        multiplier: match.multiplier ?? 1,
        arrays: Array.isArray(match.relevant_arrays) ? match.relevant_arrays : [],
        inverterId: match.inverter_link_id || null,
        mappingIssues: [],
      };
    });

    setSensorCards(cards);
    setImportStats({ unique: cards.length, duplicates: 0, alreadySaved: 0, needsReview: 0 });
    setCsvError("");
    setBanner({ tone: "", text: "Loaded saved signal mappings." });
  };

  const openMappingModal = (card) => {
    setActiveSignalId(card.tid);
    setMappingDraft({
      arrays: card.arrays.map((id) => id.toString()),
      inverterId: card.inverterId ? card.inverterId.toString() : "",
      multiplier: card.multiplier !== undefined ? String(card.multiplier) : "1",
    });
    setMapModalOpen(true);
  };

  const closeMappingModal = () => {
    setMapModalOpen(false);
    setActiveSignalId(null);
    setMappingDraft({ arrays: [], inverterId: "", multiplier: "1" });
  };

  const handleArrayDraftChange = (values) => {
    setMappingDraft((prev) => {
      const numericValues = values.map((value) => Number(value));
      const allowedInverters = new Set(
        sortedInverterOptions
          .filter((inv) => numericValues.includes(inv.array_id))
          .map((inv) => inv.id)
      );
      const nextInverter = prev.inverterId && allowedInverters.has(Number(prev.inverterId)) ? prev.inverterId : "";
      return { arrays: values, inverterId: nextInverter, multiplier: prev.multiplier };
    });
  };

  const applyMapping = () => {
    if (!activeSignalId) return;
    const arrays = mappingDraft.arrays.map((value) => Number(value)).filter((value) => !Number.isNaN(value));
    const inverterId = mappingDraft.inverterId ? Number(mappingDraft.inverterId) : null;
    const multiplier = sanitizeMultiplier(mappingDraft.multiplier);

    setSensorCards((prev) => {
      const next = prev.map((card) =>
        card.tid === activeSignalId ? { ...card, arrays, inverterId, multiplier, mappingIssues: [] } : card
      );
      setImportStats((stats) => ({ ...stats, unique: next.length, needsReview: next.filter(hasMappingIssues).length }));
      return next;
    });
    closeMappingModal();
  };

  const removeCardLocally = (tid) => {
    setSensorCards((prev) => {
      const next = prev.filter((card) => card.tid !== tid);
      setImportStats((stats) => ({ ...stats, unique: next.length, needsReview: next.filter(hasMappingIssues).length }));
      return next;
    });
  };

  const handleDeleteCard = async (card) => {
    if (card.id) {
      if (!window.confirm(`Delete saved mapping for "${card.tid}"?`)) return;
      try {
        await deleteSignalMatch(card.id);
        removeCardLocally(card.tid);
        await refreshSiteMatches(selectedSite);
        setBanner({ tone: "success", text: `Deleted mapping for ${card.tid}.` });
      } catch (err) {
        console.error("Failed to delete mapping", err);
        setBanner({ tone: "error", text: err.response?.data?.detail || "Unable to delete mapping." });
      }
      return;
    }

    removeCardLocally(card.tid);
  };

  const handleSaveAll = async () => {
    if (saveDisabled) return;
    const cardsToPersist = readyCards;
    setSaving(true);
    setBanner({ tone: "", text: "" });

    try {
      let savedCount = 0;
      const persistedTids = new Set();
      const failures = [];

      for (const card of cardsToPersist) {
        const type = getSignalTypeForCard(card);
        if (!type) continue;
        const payload = {
          site_id: selectedSite,
          signal_type_id: card.signalTypeId || type.id,
          signal_name: "",
          source_name: card.tid,
          multiplier: sanitizeMultiplier(card.multiplier),
          relevant_arrays: card.arrays,
          inverter_link_id: card.inverterId || null,
        };

        try {
          if (card.id) {
            await updateSignalMatch(card.id, payload);
          } else {
            await createSignalMatch(payload);
          }
          savedCount += 1;
          persistedTids.add(card.tid);
        } catch (err) {
          console.error("Failed to persist mapping", err);
          failures.push(err.response?.data?.detail || `Unable to save ${card.tid}.`);
        }
      }

      if (persistedTids.size > 0) {
        setSensorCards((prev) => {
          const next = prev.filter((card) => !persistedTids.has(card.tid));
          setImportStats((stats) => ({ ...stats, unique: next.length, needsReview: next.filter(hasMappingIssues).length }));
          return next;
        });
        await refreshSiteMatches(selectedSite);
      }

      if (failures.length > 0) {
        const prefix = savedCount > 0 ? `${savedCount} mapping${savedCount === 1 ? "" : "s"} saved, but ` : "";
        setBanner({ tone: "error", text: `${prefix}${failures[0]}` });
      } else if (savedCount > 0) {
        setBanner({
          tone: "success",
          text: `${savedCount} mapping${savedCount === 1 ? "" : "s"} saved successfully.`,
        });
      } else {
        setBanner({ tone: "", text: "No mappings were ready to save." });
      }
    } catch (err) {
      console.error("Failed to save mappings", err);
      setBanner({ tone: "error", text: err.response?.data?.detail || "Unable to save mappings." });
    } finally {
      setSaving(false);
    }
  };

  const activeSignal = sensorCards.find((card) => card.tid === activeSignalId);
  const numericDraftArrays = mappingDraft.arrays
    .map((value) => Number(value))
    .filter((value) => !Number.isNaN(value));
  const filteredInverters = numericDraftArrays.length
    ? (() => {
        const allowed = new Set(numericDraftArrays);
        return sortedInverterOptions.filter((inv) => allowed.has(inv.array_id));
      })()
    : [];

  const missingTypes = sensorCards.filter((card) => !getSignalTypeForCard(card)).length;

  const mappingUnavailable = loadingTopology || arrayOptions.length === 0;

  return (
    <div className="flex h-full min-h-screen flex-col bg-slate-50/60">
      <div className="sticky top-0 z-10 max-h-[50vh] overflow-y-auto border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-5 py-4">
          <header>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-500">
              SCADA Signal Interpretation
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-slate-900">Map SCADA tags to standard signal types</h1>
            <p className="mt-1 text-xs text-slate-600">
              These mappings are required before SCADA CSV files can be ingested.
            </p>
          </header>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
              <label className="text-xs font-semibold text-slate-700" htmlFor="site-select">
                Select site
              </label>
              <SiteCombobox
                id="site-select"
                sites={sites}
                valueId={selectedSite}
                query={siteQuery}
                onQueryChange={setSiteQuery}
                onChange={(site) => setSelectedSite(site ? Number(site.id) : null)}
              />
            </div>
            <div className="rounded-2xl border border-slate-900/20 bg-slate-900 text-white">
              <div className="px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-slate-300">Signals loaded</p>
                <p className="mt-1 text-2xl font-semibold">{importStats.unique}</p>
                <p className="mt-2 text-xs text-slate-300">
                  {sensorCards.length === 0 ? "Awaiting CSV import" : "Ready for review"}
                </p>
                {importStats.duplicates > 0 && (
                  <p className="mt-1 text-xs text-amber-200">Duplicates skipped: {importStats.duplicates}</p>
                )}
                {importStats.alreadySaved > 0 && (
                  <p className="mt-1 text-xs text-amber-200">
                    Existing mappings skipped: {importStats.alreadySaved}
                  </p>
                )}
                {importStats.needsReview > 0 && (
                  <p className="mt-1 text-xs text-amber-200">
                    Manual review required: {importStats.needsReview}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Load signal points from CSV</h2>
                <p className="text-xs text-slate-600">
                  CSV Format: <span className="font-mono text-xs">tid,name_short,multiplier</span>
                </p>
                <p className="text-xs text-slate-500">
                  Optional columns: <span className="font-mono text-xs">tr_array</span> and{" "}
                  <span className="font-mono text-xs">inv_array</span>
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button type="button" onClick={openFileDialog} className="bg-slate-800 hover:bg-slate-900" disabled={!selectedSite}>
                  Select file
                </Button>
                <Button
                  type="button"
                  onClick={handleLoadSaved}
                  disabled={!selectedSite || matchesLoading}
                  className="bg-slate-800 hover:bg-slate-900 disabled:bg-slate-200 disabled:text-slate-400"
                >
                  {matchesLoading ? "Loading..." : "Load saved mappings"}
                </Button>
                <Button
                  type="button"
                  disabled={saveDisabled}
                  onClick={handleSaveAll}
                  className="bg-blue-700 hover:bg-blue-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
              </div>
            </div>
            {!selectedSite && (
              <p className="mt-2 text-sm text-amber-600">Select a site before loading signal points.</p>
            )}
            {csvError && <p className="mt-2 text-sm text-rose-600">{csvError}</p>}
            {banner.text && (
              <p
                className={`mt-2 text-sm ${
                  banner.tone === "success"
                    ? "text-emerald-600"
                    : banner.tone === "warning"
                    ? "text-amber-600"
                    : banner.tone === "error"
                    ? "text-rose-600"
                    : "text-slate-600"
                }`}
              >
                {banner.text}
              </p>
            )}
            {!saveDisabled && readyCards.length < sensorCards.length && (
              <p className="mt-2 text-sm text-slate-600">
                {`Saving ${readyCards.length} of ${sensorCards.length} cards.`}
              </p>
            )}
            {importStats.needsReview > 0 && (
              <p className="mt-2 text-sm text-amber-600">
                {importStats.needsReview} imported mapping{importStats.needsReview === 1 ? "" : "s"} must be corrected
                manually before they can be saved.
              </p>
            )}
            {missingTypes > 0 && (
              <p className="mt-2 text-sm text-rose-600">
                {missingTypes} short code{missingTypes === 1 ? " is" : "s are"} not registered in Signal Settings.
              </p>
            )}
          </div>
          {sensorCards.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Signal filters</h2>
                  <p className="text-xs text-slate-600">Filter cards before applying mapping changes.</p>
                </div>
                <div className="flex flex-1 flex-col gap-3 lg:flex-row lg:items-center lg:justify-end">
                  <div className="flex flex-wrap gap-2">
                    {availableTypeFilters.map((code) => {
                      const active = typeFilters.includes(code);
                      return (
                        <button
                          key={code}
                          type="button"
                          onClick={() =>
                            setTypeFilters((prev) =>
                              active ? prev.filter((entry) => entry !== code) : [...prev, code]
                            )
                          }
                          className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                            active ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600"
                          }`}
                        >
                          {code}
                        </button>
                      );
                    })}
                  </div>
                  <div className="w-full lg:w-64">
                    <Input
                      placeholder="Filter by signal name..."
                      value={nameFilter}
                      onChange={(event) => setNameFilter(event.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-6xl space-y-4 px-6 py-6">
          {sensorCards.length === 0 ? (
            <EmptyState hasSite={Boolean(selectedSite)} />
          ) : filteredCards.length === 0 ? (
            <div className="rounded-md bg-slate-100 px-4 py-6 text-center text-sm text-slate-600">
              No signals match the current filters.
            </div>
          ) : (
            filteredCards.map((card) => {
              const type = getSignalTypeForCard(card);
              return (
                <SignalCard
                  key={card.tid}
                  card={card}
                  signalType={type}
                  arrayNameMap={arrayNameMap}
                  inverterNameMap={inverterNameMap}
                  onMap={() => openMappingModal(card)}
                  onDelete={() => handleDeleteCard(card)}
                  mappingDisabled={mappingUnavailable}
                />
              );
            })
          )}
        </div>
      </div>

      <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleCsvSelection} />

      <Dialog open={mapModalOpen} onOpenChange={(open) => !open && closeMappingModal()}>
        <DialogContent className="w-full max-w-2xl">
          <DialogHeader>
            <DialogTitle>Map signal</DialogTitle>
            {activeSignal && <p className="text-sm text-slate-500">{activeSignal.tid}</p>}
          </DialogHeader>
          <DialogContentBody>
            <form className="space-y-5" onSubmit={(e) => e.preventDefault()}>
              <div>
                <label className="text-sm font-semibold text-slate-700">Transformer array(s)</label>
                <select
                  multiple
                  size={8}
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  value={mappingDraft.arrays}
                  onChange={(e) =>
                    handleArrayDraftChange(Array.from(e.target.selectedOptions, (opt) => opt.value))
                  }
                >
                  {sortedArrayOptions.map((arr) => (
                    <option key={arr.id} value={arr.id}>
                      {arr.array_name} {arr.transformer_name ? `(${arr.transformer_name})` : ""}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-slate-500">Hold Ctrl (Cmd on macOS) to pick multiple entries.</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Inverter array (optional)</label>
                <select
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  value={mappingDraft.inverterId}
                  disabled={filteredInverters.length === 0}
                  onChange={(e) => setMappingDraft((prev) => ({ ...prev, inverterId: e.target.value }))}
                >
                  <option value="">
                    {filteredInverters.length === 0 ? "Select transformer arrays first" : "Select inverter array"}
                  </option>
                  {filteredInverters.map((inv) => (
                    <option key={inv.id} value={inv.id}>
                      {inv.inv_array_name || inv.array_name}
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-slate-500">
                  Only inverter arrays belonging to the selected transformer arrays are shown.
                </p>
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Multiplier</label>
                <input
                  type="number"
                  step="0.0001"
                  className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  value={mappingDraft.multiplier}
                  onChange={(e) => setMappingDraft((prev) => ({ ...prev, multiplier: e.target.value }))}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  onClick={closeMappingModal}
                >
                  Cancel
                </button>
                <Button
                  type="button"
                  disabled={mappingDraft.arrays.length === 0}
                  onClick={applyMapping}
                >
                  Apply mapping
                </Button>
              </div>
            </form>
          </DialogContentBody>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SignalCard({ card, signalType, arrayNameMap, inverterNameMap, onMap, onDelete, mappingDisabled }) {
  const arrayBadges = card.arrays
    .map((id) => ({ id, label: arrayNameMap.get(id) || `Array ${id}` }))
    .sort((a, b) => compareArrayNames(a.label, b.label));
  const inverterBadge = card.inverterId && inverterNameMap.get(card.inverterId)
    ? [{ id: card.inverterId, label: inverterNameMap.get(card.inverterId) }]
    : [];
  const status = getCardStatus(card, signalType);

  return (
    <div className="rounded-[32px] border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-lg font-semibold text-slate-900">{card.tid}</p>
          <p className="text-sm text-slate-500">{signalType?.name || "Signal type not registered"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <span className={`rounded-full px-3 py-1 text-sm font-semibold ${signalType ? "bg-sky-50 text-sky-700" : "bg-rose-50 text-rose-700"}`}>
            {card.shortName || "N/A"}
          </span>
          <span className="text-sm font-medium text-slate-600">
            Multiplier {formatMultiplier(card.multiplier)}
          </span>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${status.className}`}>
            {status.label}
          </span>
          <button
            type="button"
            className="text-sm font-semibold text-rose-500 hover:text-rose-600"
            onClick={onDelete}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_auto_1fr]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Transformer array(s)</p>
          <ChipList items={arrayBadges} placeholder={mappingDisabled ? "Loading arrays..." : "Not mapped yet"} />
        </div>
        <div className="flex flex-col items-center justify-center text-slate-500">
          <button
            type="button"
            className={`flex h-16 w-16 items-center justify-center rounded-full border-2 border-dashed text-3xl font-semibold transition ${
              mappingDisabled
                ? "cursor-not-allowed border-slate-200 text-slate-300"
                : "border-slate-300 text-slate-500 hover:border-slate-500 hover:text-slate-700"
            }`}
            onClick={onMap}
            disabled={mappingDisabled}
            aria-label="Map signal"
          >
            +
          </button>
          <span className="mt-2 text-sm font-medium text-slate-600">Map signal</span>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Inverter</p>
          <ChipList items={inverterBadge} placeholder="Not mapped yet" />
        </div>
      </div>
      {hasMappingIssues(card) && (
        <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          {card.mappingIssues.map((issue) => (
            <p key={issue}>{issue}</p>
          ))}
        </div>
      )}
    </div>
  );
}

function ChipList({ items, placeholder }) {
  if (!items || items.length === 0) {
    return <p className="mt-2 text-sm text-slate-400">{placeholder}</p>;
  }
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {items.map((item) => (
        <span key={item.id} className="rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-700">
          {item.label}
        </span>
      ))}
    </div>
  );
}

function EmptyState({ hasSite }) {
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-white/70 px-10 py-16 text-center">
      <p className="text-xl font-semibold text-slate-800">No sensor points loaded</p>
      <p className="mt-2 text-sm text-slate-500">
        {hasSite
          ? "Select the CSV file above or load saved mappings to begin."
          : "Pick a site to unlock CSV import."}
      </p>
    </div>
  );
}

function SiteCombobox({ id, sites, valueId, query, onQueryChange, onChange }) {
  const selectedSite = sites.find((site) => site.id === valueId) || null;
  const filtered = query ? sites.filter((site) => site.name.toLowerCase().includes(query.toLowerCase())) : sites;

  return (
    <Combobox value={selectedSite} onChange={onChange} nullable>
      <div className="relative mt-2">
        <Combobox.Input
          id={id}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          displayValue={(site) => site?.name || ""}
          placeholder="Start typing a site..."
          onChange={(event) => onQueryChange(event.target.value)}
        />
        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-500">
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Combobox.Button>
        <Combobox.Options className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">No sites found</div>
          ) : (
            filtered.map((site) => (
              <Combobox.Option
                key={site.id}
                value={site}
                className={({ active }) =>
                  `cursor-pointer px-3 py-2 text-sm ${active ? "bg-blue-600 text-white" : "text-slate-800"}`
                }
              >
                {site.name}
              </Combobox.Option>
            ))
          )}
        </Combobox.Options>
      </div>
    </Combobox>
  );
}

function DialogContentBody({ children }) {
  return <div className="mt-4 space-y-4">{children}</div>;
}

function dedupeById(rows) {
  const seen = new Set();
  return rows.filter((row) => {
    if (seen.has(row.id)) return false;
    seen.add(row.id);
    return true;
  });
}

function parseSignalCsv(
  content,
  {
    existingSources = new Set(),
    arrayLookupByName = new Map(),
    inverterLookupByName = new Map(),
  } = {}
) {
  if (!content) {
    throw new Error("CSV file is empty.");
  }
  const lines = String(content)
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  if (lines.length < 2) {
    throw new Error("CSV must include a header row and at least one record.");
  }
  const headerCells = lines[0].split(",").map((cell) => normalizeLookupKey(cell));
  const headerMap = Object.fromEntries(headerCells.map((name, index) => [name, index]));
  const missing = REQUIRED_HEADERS.filter((header) => !(header in headerMap));
  if (missing.length) {
    throw new Error(`Missing column(s): ${missing.join(", ")}.`);
  }

  const seen = new Set();
  const records = [];
  let duplicates = 0;
  let alreadySaved = 0;

  for (let i = 1; i < lines.length; i += 1) {
    const row = lines[i];
    if (!row) continue;
    const cells = row.split(",").map((cell) => stripBom(cell).trim());
    const tid = cells[headerMap.tid];
    if (!tid) continue;
    const normalizedTid = tid.trim();
    const normalizedKey = normalizedTid.toLowerCase();
    if (existingSources.has(normalizedKey)) {
      alreadySaved += 1;
      continue;
    }
    if (seen.has(normalizedKey)) {
      duplicates += 1;
      continue;
    }
    seen.add(normalizedKey);
    const shortName = cells[headerMap.name_short] || "";
    const multiplier = sanitizeMultiplier(cells[headerMap.multiplier]);
    const mapping = resolveImportedMapping({
      rowNumber: i + 1,
      trArrayValue: headerMap.tr_array !== undefined ? cells[headerMap.tr_array] : "",
      invArrayValue: headerMap.inv_array !== undefined ? cells[headerMap.inv_array] : "",
      arrayLookupByName,
      inverterLookupByName,
    });
    records.push({
      tid: normalizedTid,
      shortName,
      multiplier,
      arrays: mapping.arrays,
      inverterId: mapping.inverterId,
      signalTypeId: null,
      mappingIssues: mapping.issues,
    });
  }

  return { records, duplicates, alreadySaved };
}

function sanitizeMultiplier(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 1;
}

function getCardStatus(card, signalType) {
  if (hasMappingIssues(card)) {
    return { label: "Needs mapping review", className: "bg-amber-50 text-amber-700" };
  }
  if (!signalType) {
    return { label: "Unknown signal type", className: "bg-rose-50 text-rose-700" };
  }
  if (!card.arrays.length) {
    return { label: "Select transformer arrays", className: "bg-amber-50 text-amber-700" };
  }
  return { label: "Ready", className: "bg-emerald-50 text-emerald-700" };
}

function formatMultiplier(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "1";
  return numeric.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

function compareArrayNames(a, b) {
  const left = a || "";
  const right = b || "";
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: "base" });
}

function hasMappingIssues(card) {
  return Array.isArray(card?.mappingIssues) && card.mappingIssues.length > 0;
}

function buildNameLookup(rows, getName) {
  const lookup = new Map();
  rows.forEach((row) => {
    const key = normalizeLookupKey(getName(row));
    if (!key) return;
    const bucket = lookup.get(key) || [];
    bucket.push(row);
    lookup.set(key, bucket);
  });
  return lookup;
}

function resolveImportedMapping({
  rowNumber,
  trArrayValue,
  invArrayValue,
  arrayLookupByName,
  inverterLookupByName,
}) {
  const issues = [];
  const arrayIds = new Set();
  const transformerNames = splitImportNames(trArrayValue);
  const inverterNames = splitImportNames(invArrayValue);

  transformerNames.forEach((name) => {
    const matches = arrayLookupByName.get(normalizeLookupKey(name)) || [];
    if (matches.length === 1) {
      arrayIds.add(matches[0].id);
      return;
    }
    if (matches.length > 1) {
      issues.push(`Row ${rowNumber}: transformer array "${name}" is ambiguous for this site.`);
      return;
    }
    issues.push(`Row ${rowNumber}: transformer array "${name}" was not found for this site.`);
  });

  let inverterId = null;
  if (inverterNames.length > 1) {
    issues.push(
      `Row ${rowNumber}: multiple inverter arrays were provided in inv_array. Select one inverter manually before saving.`
    );
  } else if (inverterNames.length === 1) {
    const inverterName = inverterNames[0];
    const matches = inverterLookupByName.get(normalizeLookupKey(inverterName)) || [];
    if (matches.length === 1) {
      const inverter = matches[0];
      inverterId = inverter.id;
      if (arrayIds.size === 0) {
        arrayIds.add(inverter.array_id);
      } else if (!arrayIds.has(inverter.array_id)) {
        issues.push(
          `Row ${rowNumber}: inverter array "${inverterName}" does not belong to the selected transformer array mapping.`
        );
      }
    } else if (matches.length > 1) {
      issues.push(`Row ${rowNumber}: inverter array "${inverterName}" is ambiguous for this site.`);
    } else {
      issues.push(`Row ${rowNumber}: inverter array "${inverterName}" was not found for this site.`);
    }
  }

  return { arrays: Array.from(arrayIds), inverterId, issues };
}

function splitImportNames(value) {
  return String(value || "")
    .trim()
    .split(/\s+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function normalizeLookupKey(value) {
  return stripBom(value).trim().toLowerCase();
}

function stripBom(value) {
  return String(value || "").replace(/^\uFEFF/, "");
}
